import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { AuthService } from "app/core/auth/service/auth.service";
import { environment } from "environments/environment";
import { NGXLogger } from "ngx-logger";
import { BehaviorSubject, from, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";

export class AuthInterceptor implements HttpInterceptor {
  private readonly ignoreUrls: string[] = ["svg"];
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | undefined>(
    undefined,
  );

  constructor(
    private authService: AuthService,
    private logger: NGXLogger,
  ) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isDomainUrl: boolean = request.url.includes(environment.DOMAIN);
    const isAccountUrl: boolean = request.url.includes("/account");

    const accessToken: string | undefined = this.authService.getAccessToken();

    if ((!isAccountUrl && !isDomainUrl) || this.isIgnoreUrl(request)) {
      return next.handle(request);
    }

    const authRequest: HttpRequest<any> = request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.error("HttpErrorResponse", error);
        if (error.status === 401 || error.status === 0) {
          // Only refresh if token is invalid (expired)
          if (!this.authService.isTokenValid()) {
            return this.handle401Error(authRequest, next);
          }
        }

        // Token is valid -> assume permission issue
        return throwError(() => error);
      }),
    );
  }

  private isIgnoreUrl(request: HttpRequest<any>): boolean {
    return this.ignoreUrls.some((url) => request.url.includes(url));
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(undefined);

      return from(this.authService.updateToken(-1)).pipe(
        switchMap((isRefreshed) => {
          this.isRefreshing = false;

          if (!isRefreshed) {
            // Refresh failed and token is still invalid -> logout
            this.authService.logout();
            this.refreshTokenSubject.next(undefined);
            return throwError(() => new Error("Token refresh failed"));
          }

          // Refresh succeeded -> retry original request with new token
          const newAccessToken = this.authService.getAccessToken();
          this.refreshTokenSubject.next(newAccessToken);

          const authRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${newAccessToken}`,
            },
          });

          return next.handle(authRequest);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(undefined);

          // Something went wrong during refresh -> logout
          this.authService.logout();
          return throwError(() => error);
        }),
      );
    } else {
      // Wait until token is refreshed
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          const authRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
          return next.handle(authRequest);
        }),
      );
    }
  }
}
